import { Color } from '@persona-id/design-tokens';
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import { TransitionDuration } from 'design-system/variables/transitions';
import { NAVBAR_ID } from 'inquiry/components/ConnectedNavbar/ConnectedNavbar';
import AboveFrameHint from 'inquiry/components/ScannerOverlay/AboveFrameHint';
import CaptureFrameLabel from 'inquiry/components/ScannerOverlay/CaptureFrameLabel';
import InFrameHint from 'inquiry/components/ScannerOverlay/InFrameHint';
import OverlayImage from 'inquiry/components/ScannerOverlay/OverlayImage';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import useIsMounted from 'lib/hooks/useIsMounted';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import {
  SerializedStepGovernmentIdStyles,
  StepGovernmentIdConfig,
} from 'lib/interfaces/StepsConfig';
import { TextComponentStyles } from 'lib/interfaces/ui-components';
import { GovernmentIdOverlay, GovernmentIdSide } from 'lib/interfaces/verificationConfig';

export const COLLAPSE_ANIMATION_DURATION_SECONDS = 1;
export const FLIP_ANIMATION_DURATION_SECONDS = 1;
const BORDER_RADIUS = 8;
const BORDER_WIDTH = 3;

const getBorderRadius = (
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) => {
  return sizeCss(styles?.border?.radius) ?? `${BORDER_RADIUS}px`;
};

const getBorderWidth = (
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) => sizeCss(styles?.border?.width?.top) ?? `${BORDER_WIDTH}px`;
const getBorderColor = (
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) => colorCss(styles?.border?.color) ?? Color.PrimaryWhite;
const getInnerBorderRadius = (
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) => `calc(${getBorderRadius(styles)} - ${getBorderWidth(styles)})`;

// The Percent Width/Height the visible capture window is compared to the size of the real window
export const VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO = 0.8;
export const VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO = 0.8;

// Ratio of regular capture window to short barcode capture window is 66/35
// 0.4242 = 35/66 * 0.8
export const SHORT_BARCODE_VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO = 0.4242;

const OVERLAY_HINT_VERTICAL_PADDING = 14;

const HAS_REQUEST_IDLE_CALLBACK = window.requestIdleCallback != null;

const FullscreenPage = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

interface ScannerOverlayProps {
  assets: StepGovernmentIdConfig['assets']['capture-page'];
  capturedImageUrl?: string;
  governmentIdClass: GovernmentIdClass;
  requiresSide: GovernmentIdSide;
  captureOverlay?: GovernmentIdOverlay | null;
  mirrored?: boolean;

  captureFrameLabel: string;
  hint?: string | null;
  prompt?: string | null;

  capturing?: boolean;

  overlayRef?: RefObject<HTMLDivElement | undefined>;
  overlayCaptureWindowRef?: RefObject<HTMLDivElement | undefined>;
  previewViewfinderRef?: RefObject<HTMLDivElement | undefined>;

  hasDetectedQuad?: boolean;
  styles?: {
    hint: TextComponentStyles;
    base: SerializedStepGovernmentIdStyles['base'];
    captureBox: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'];
    captureOverlayHeaderButton: SerializedStepGovernmentIdStyles['captureOverlayHeaderButton'];
  };

  isAnimated?: boolean;
  isShortBarcodeOverlay?: boolean;

  renderUnderOverlayComponents?: () => ReactNode;
  renderCaptureWindowContents?: () => ReactNode;

  reverseSide?: GovernmentIdSide;
  reverseCaptureFrameLabel?: string;

  collapsed?: boolean;
  expandOnMount?: boolean;
  flipped?: boolean;

  onReady?: () => void;
}

const ScannerOverlay = (props: ScannerOverlayProps) => {
  const defaultOverlayRef = useRef<HTMLDivElement | undefined>();

  const navbarButtonColor = colorCss(props?.styles?.captureOverlayHeaderButton?.stroke?.color);

  return (
    <>
      <FullscreenOverlayGlobalStyles svgColor={navbarButtonColor} />
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <FullscreenPage ref={props.overlayRef ?? defaultOverlayRef}>
        <InnerOverlay {...props} />
      </FullscreenPage>
    </>
  );
};

/**
 * Ignore custom nav background color here -- always make it appear black by being transparent.
 * We DO allow custom icon colors, though, for parity with mobile SDKs.
 *
 * @see front-end/verify/components/Navbar/Navbar.tsx
 **/
const FullscreenOverlayGlobalStyles = createGlobalStyle<{ svgColor?: string | null }>`
  nav#${NAVBAR_ID} {
    background-color: unset;

    svg {
      path {
        ${({ svgColor }) => {
          // default override color of back button to always be white
          return `stroke: ${svgColor ?? Color.PrimaryWhite};`;
        }}
      }
    }
  }
`;

// wrapper component contains both the visual capture window and the larger real capture window
// we use the scrollHeight to compute how tall the imagecontainer needs to get during collapse animation,
// so we dont' want to put the real capture window inside of the image container as that will affect scrollHeight
const ImageContainerWrapper = styled.div`
  position: relative;
`;

const ImageContainer = styled.div<{ height: string | number; mirrored?: boolean }>`
  height: ${({ height }) => height};
  position: relative;
  transform: ${({ mirrored }) => (mirrored ? 'scale(-1, 1)' : null)};
  transition: all ${COLLAPSE_ANIMATION_DURATION_SECONDS}s;
`;

const ImageContainerSizer = styled('div')<{ isShortBarcodeOverlay?: boolean }>`
  // Aspect ratio different for MRZ
  top: 0;
  left: 0;
  padding-bottom: ${({ isShortBarcodeOverlay }) => (isShortBarcodeOverlay ? '35%' : '66%')};
  overflow: hidden;
`;

const CaptureFrame = styled('div')<{ isReverseSide?: boolean; isAnimated?: boolean }>`
  backface-visibility: hidden;
  height: 100%;
  width: 100%;
  overflow: hidden;
  // need to set a transform even if it isn't the reverse side for backface visibilty to work on Safari
  // probably has to do with forcing hardware acceleration?
  transform: ${({ isAnimated, isReverseSide }) =>
    isReverseSide ? 'rotateY(180deg)' : isAnimated ? 'scale(1)' : null};
  position: ${({ isReverseSide }) => (isReverseSide ? 'absolute' : null)};
  top: ${({ isReverseSide }) => (isReverseSide ? 0 : null)};
`;

const VerticalPositioner = styled('div')<{
  animated?: boolean;
  collapsed?: boolean;
  collapsedVerticalOffset?: string | null;
  isShortBarcodeOverlay?: boolean;
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'];
}>`
  position: absolute;
  top: calc(
    ${({ collapsed, isShortBarcodeOverlay }) =>
        collapsed ? '50%' : isShortBarcodeOverlay ? '35%' : '25%'} -
      ${({ styles }) => getBorderWidth(styles)}
  );
  background: ${({ collapsed }) => (collapsed ? 'black' : 'transparent')};
  width: calc(80% + 2 * (${({ styles }) => getBorderWidth(styles)}));
  margin-left: calc(10% - ${({ styles }) => getBorderWidth(styles)});
  margin-right: calc(10% - ${({ styles }) => getBorderWidth(styles)});
  perspective: ${({ animated }) => (animated ? '1000px' : null)};
  transform: ${({ animated, collapsed, collapsedVerticalOffset }) =>
    animated && collapsed ? `translate3d(0, ${collapsedVerticalOffset ?? '-50%'}, 0)` : null};
  transition: ${({ animated }) =>
    animated ? `all ${COLLAPSE_ANIMATION_DURATION_SECONDS}s` : null};
`;

const FlipCard = styled('div')<{
  animated?: boolean;
  flipped?: boolean;
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'];
}>`
  border: ${({ styles }) => `${getBorderWidth(styles)} solid ${getBorderColor(styles)}`};
  border-radius: ${({ styles }) => getBorderRadius(styles)};
  position: relative;
  width: 100%;
  height: 100%;
  // overflow:hidden prevents content inside from cutting into the outer border
  // when flipping don't set overflow:hidden, because overflow:hidden breaks backface visbility for flip animation
  // this should be fine because flipping only occurs when we are in collapsed mode,
  // and the hint card has border radius set appropriately to not cut into the outer border
  overflow: ${({ flipped }) => (flipped ? null : 'hidden')};

  transform-style: ${({ animated }) => (animated ? 'preserve-3d' : null)};
  transform: ${({ animated, flipped }) => (animated && flipped ? 'rotateY(180deg)' : null)};
  transition: ${({ animated }) =>
    animated ? `transform ${FLIP_ANIMATION_DURATION_SECONDS}s` : null};
`;

const UnderOverlayComponentsWrapper = styled('div')<{ visible: boolean }>`
  position: absolute;
  bottom: -16px;
  transform: translateY(100%);
  z-index: 1;

  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  width: 100%;
`;

const RealCaptureWindow = styled.div<{
  visibleToRealCaptureWindowHeightRatio: number;
  visibleToRealCaptureWindowWidthRatio: number;
}>`
  position: absolute;
  overflow: hidden;

  top: ${({ visibleToRealCaptureWindowHeightRatio }) =>
    (100 - 100 / visibleToRealCaptureWindowHeightRatio) / 2}%;
  height: ${({ visibleToRealCaptureWindowHeightRatio }) =>
    100 / visibleToRealCaptureWindowHeightRatio}%;
  left: ${({ visibleToRealCaptureWindowWidthRatio }) =>
    (100 - 100 / visibleToRealCaptureWindowWidthRatio) / 2}%;
  width: ${({ visibleToRealCaptureWindowWidthRatio }) =>
    100 / visibleToRealCaptureWindowWidthRatio}%;

  video {
    object-fit: cover;

    height: 100%;
    width: 100%;
  }
`;

const FrontFlipCardSide = (
  props: ScannerOverlayProps & {
    inFrameText?: string | null;
    imageContainerHeight: string | number;
    hideGuide?: boolean;
    captureFrameLabelHeight?: string | number;
    onTransitionEnd?: () => void;
    imageContainerSizerRef?: RefObject<HTMLDivElement>;
    captureFrameLabelRef?: RefObject<HTMLDivElement>;
  },
) => {
  return (
    <CaptureFrame isReverseSide={false} isAnimated={props.isAnimated}>
      <ImageContainerWrapper>
        <ImageContainer
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          ref={props.previewViewfinderRef}
          mirrored={props.mirrored}
          height={props.imageContainerHeight}
          onTransitionEnd={() => {
            props.onTransitionEnd?.();
          }}
        >
          <RealCaptureWindow
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            ref={props.overlayCaptureWindowRef}
            visibleToRealCaptureWindowWidthRatio={VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO}
            visibleToRealCaptureWindowHeightRatio={
              props.isShortBarcodeOverlay
                ? SHORT_BARCODE_VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO
                : VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO
            }
          >
            {props.renderCaptureWindowContents?.()}
          </RealCaptureWindow>
          <ImageContainerSizer
            ref={props.imageContainerSizerRef}
            isShortBarcodeOverlay={props.isShortBarcodeOverlay}
          />
          <OverlayImage
            captureOverlay={props.captureOverlay ?? null}
            isShortBarcodeOverlay={props.isShortBarcodeOverlay}
            hideGuide={props.hideGuide}
            highlightCorners={!!props.hasDetectedQuad}
            showFlash={props.capturing}
          />
        </ImageContainer>
        {!props.isShortBarcodeOverlay && <InFrameHint text={props.inFrameText} />}
      </ImageContainerWrapper>
      <CaptureFrameLabel
        collapsed={false}
        borderRadius={getInnerBorderRadius(props.styles?.captureBox)}
        ref={props.captureFrameLabelRef}
        styles={props.styles?.hint}
        assets={props.assets}
        side={props.requiresSide}
        idClass={props.governmentIdClass}
        text={props.captureFrameLabel}
        height={props.captureFrameLabelHeight}
      />
    </CaptureFrame>
  );
};

const ReverseFlipCardSide = (
  props: ScannerOverlayProps & {
    captureFrameLabelHeight?: number | string;
    captureFrameLabelRef?: RefObject<HTMLDivElement>;
  },
) => {
  if (!props.reverseSide) {
    return null;
  }

  return (
    <CaptureFrame isReverseSide={true} isAnimated={props.isAnimated} aria-hidden='true'>
      <CaptureFrameLabel
        ref={props.captureFrameLabelRef}
        collapsed={false}
        borderRadius={getInnerBorderRadius(props.styles?.captureBox)}
        styles={props.styles?.hint}
        assets={props.assets}
        side={props.reverseSide}
        idClass={props.governmentIdClass}
        text={props.reverseCaptureFrameLabel}
      />
    </CaptureFrame>
  );
};

const InnerOverlay = (props: ScannerOverlayProps & { flipped?: boolean }) => {
  const imageContainerSizerRef = useRef<HTMLDivElement>(null);
  // initial state of window is collapsed if props.collapsed is true or if we are supposed to display expand animation on mount
  const [collapsed, setCollapsed] = useState(props.collapsed || props.expandOnMount);
  // initial height of capture window is 0 if we are starting in a collapsed state, otherwise fit the content.
  const [imageContainerHeight, setHeight] = useState(collapsed ? 0 : 'auto');

  const forwardCaptureFrameLabelRef = useRef<HTMLDivElement>(null);
  const reverseCaptureFrameLabelRef = useRef<HTMLDivElement>(null);

  const [flipped, setFlipped] = useState(props.flipped);
  const [captureFrameLabelHeight, setCaptureFrameLabelHeight] = useState<number | string>('auto');

  const [collapsedVerticalOffset, setCollapsedVerticalOffset] = useState<string | null>(null);

  const defaultViewFinderRef = useRef<HTMLDivElement>(null);

  const viewFinderRef = props.previewViewfinderRef ?? defaultViewFinderRef;

  useEffect(() => {
    // The height is determined by imageContainerSizerRef for Dynamic Gov ID
    const captureWindowHeight = props.captureOverlay
      ? imageContainerSizerRef.current?.clientHeight
      : viewFinderRef.current?.scrollHeight;

    if (isMounted()) {
      // if the current state of the capture window is not collapsed, but the props are trying to set it to collapsed,
      // first set the height to the computed height, then set it to 0 in the next update cycle.
      // we do this because height: auto cannot transition properly.
      if (props.collapsed && !collapsed) {
        setHeight(captureWindowHeight + 'px');
        if (HAS_REQUEST_IDLE_CALLBACK) {
          window.requestIdleCallback(
            () => {
              setHeight(0);
              // set the collapsed vertical offset to be 50% of the collapsed height manually
              // this is because the css transition doesn't work in safari since it uses
              // the initial height to calculate translateY(-50%) instead of the final height
              setCollapsedVerticalOffset(
                `${
                  -(
                    // prettier-ignore
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    (forwardCaptureFrameLabelRef.current?.scrollHeight +
                      OVERLAY_HINT_VERTICAL_PADDING * 2)
                  ) / 2
                }px`,
              );
              setCollapsed(true);
            },
            { timeout: 500 },
          );
        } else {
          setTimeout(() => {
            setHeight(0);
            setCollapsedVerticalOffset(
              `${
                -(
                  // prettier-ignore
                  /* @ts-expect-error [strict-migration] not strict; please fix! */
                  (forwardCaptureFrameLabelRef.current?.scrollHeight +
                    OVERLAY_HINT_VERTICAL_PADDING * 2)
                ) / 2
              }px`,
            );
            setCollapsed(true);
          }, 50);
        }
      } else {
        setHeight(props.collapsed ? 0 : captureWindowHeight || 'auto');

        if (props.collapsed) {
          setCollapsedVerticalOffset(
            `${
              -(
                // prettier-ignore
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                (forwardCaptureFrameLabelRef.current?.scrollHeight +
                  OVERLAY_HINT_VERTICAL_PADDING * 2)
              ) / 2
            }px`,
          );
        }

        setCollapsed(props.collapsed);
      }
    } else {
      // if this is the first render, and expandOnMount is set to true, the height is set to 0 initially.
      // Here, we set the height to the computed height of the content so it expands, and we also set the
      // state to reflect the true state of the component and set/animate its top offset.
      if (props.expandOnMount) {
        setHeight(captureWindowHeight + 'px');
        setCollapsed(false);
      } else {
        // call onReady immeidately if we aren't animating anything fron the start, otherwise the onReady will be handled by the onTransitionEnd callback
        props.onReady?.();
      }
    }
  }, [props.collapsed]);

  useEffect(() => {
    if (isMounted()) {
      if (props.flipped && !flipped) {
        // this animates the card shrinking if the text on the reverse side is shorter than the text on the forward side
        // like with the capture window height, we have to first set the height to the computed content height plus its padding, before setting it to the
        // computed height for the reverse side
        setCaptureFrameLabelHeight(
          `${
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            forwardCaptureFrameLabelRef.current?.scrollHeight + OVERLAY_HINT_VERTICAL_PADDING * 2
          }px`,
        );
        if (HAS_REQUEST_IDLE_CALLBACK) {
          window.requestIdleCallback(
            () => {
              const newHeight =
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                reverseCaptureFrameLabelRef.current?.scrollHeight +
                OVERLAY_HINT_VERTICAL_PADDING * 2;
              setCaptureFrameLabelHeight(`${newHeight}px`);
              setCollapsedVerticalOffset(`-${newHeight / 2}px`);
              setFlipped(true);
            },
            { timeout: 500 },
          );
        } else {
          setTimeout(() => {
            const newHeight =
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              reverseCaptureFrameLabelRef.current?.scrollHeight + OVERLAY_HINT_VERTICAL_PADDING * 2;
            setCaptureFrameLabelHeight(`${newHeight}px`);
            setCollapsedVerticalOffset(`-${newHeight / 2}px`);
            setFlipped(true);
          }, 50);
        }
      }
    }
  }, [props.flipped]);

  // need to set mounted state here so that it takes place after the collapsed and flipped callbacks
  // so that those functions have an accurate picture of the current component state.
  const isMounted = useIsMounted();

  // need to set mounted state here so that it takes place after the collapsed and flipped callbacks
  // so that those functions have an accurate picture of the current component state.

  const aboveFrameText = props.isShortBarcodeOverlay ? props.hint || props.prompt : props.prompt;

  const inFrameText = props.isShortBarcodeOverlay ? null : props.hint;

  const shouldHideGuide = props.capturing || props.hasDetectedQuad || !!inFrameText;

  // boolean sets certain CSS properties which may cause additional transforms rendering layers
  // needed to animate for collapsing or flipping the overlay
  const animated = props.isAnimated || props.expandOnMount || props.collapsed || props.flipped;

  return (
    <VerticalPositioner
      animated={animated}
      collapsed={collapsed}
      collapsedVerticalOffset={collapsedVerticalOffset}
      isShortBarcodeOverlay={props.isShortBarcodeOverlay}
    >
      <AboveFrameHint text={collapsed ? null : aboveFrameText} />
      <FlipCard animated={animated} flipped={flipped} styles={props.styles?.captureBox}>
        <FrontFlipCardSide
          {...props}
          previewViewfinderRef={viewFinderRef}
          imageContainerSizerRef={imageContainerSizerRef}
          captureFrameLabelRef={forwardCaptureFrameLabelRef}
          captureFrameLabelHeight={captureFrameLabelHeight}
          imageContainerHeight={imageContainerHeight}
          hideGuide={shouldHideGuide}
          inFrameText={inFrameText}
          onTransitionEnd={() => {
            // set height back to auto instead of the computed height so it we resize properly when screen resizes
            setHeight(collapsed ? 0 : 'auto');
            props.onReady?.();
          }}
          isAnimated={animated}
        />
        <ReverseFlipCardSide
          {...props}
          captureFrameLabelRef={reverseCaptureFrameLabelRef}
          captureFrameLabelHeight={captureFrameLabelHeight}
          reverseSide={props.reverseSide}
          isAnimated={animated}
        />
      </FlipCard>
      {props.renderUnderOverlayComponents && (
        <UnderOverlayComponentsWrapper visible={true}>
          {props.renderUnderOverlayComponents()}
        </UnderOverlayComponentsWrapper>
      )}
    </VerticalPositioner>
  );
};

export default ScannerOverlay;
